import { Grid, GridItem, HStack, VStack } from '@chakra-ui/react';
import { Button, Infobox, TruncatableText } from 'Atoms';
import {
  GetMaterialStandardsIdsQuery_,
  GetMFdrForMFstandardQuery_,
  GetParentAssessmentMaterialMetricsQuery_,
  GetParentMaterialityAssessmentQuery_,
  useGetMFdrForMFstandardQuery,
} from 'models';
import { Loader } from 'Molecules';
import { Dispatch, JSX, SetStateAction, useMemo } from 'react';
import { Typography } from 'Tokens';
import { FrameworkRefs, Frameworks } from '../../Assessment';
import { useParams } from 'react-router-dom';
import { MFStandardBrowserTable } from './MFStandardBrowserTable';
import { MultiFrameCategoryAndStandards } from './DataPointsBank';
import { MaterialityStatusBox } from '../DoubleMaterialityAssessment/MaterialityUtils';
import {
  Materiality,
  StandardMaterialityState,
} from '../DoubleMaterialityAssessment/DoubleMaterialityAssessment.d';
import { ArrowUpRightIcon } from 'Tokens/Icons/Direction';

export type Metric =
  GetMFdrForMFstandardQuery_['MFStandard'][0]['mfDisclosureRequirementGroups'][0]['mfDisclosureRequirements'][0]['mfDisclosureRequirementItems'][0]['metric'];

export const sortFrameworkMetrics = (a: Metric, b: Metric, currentFramework: string) => {
  const fwA = a.requirement.frameworkRef ?? '';
  const fwB = b.requirement.frameworkRef ?? '';
  const isAFromCurrent = fwA === currentFramework;
  const isBFromCurrent = fwB === currentFramework;

  if (isAFromCurrent && !isBFromCurrent) return 1;
  if (!isAFromCurrent && isBFromCurrent) return -1;

  return fwA.localeCompare(fwB);
};

export const StandardsView = ({
  frameworkList,
  selectedFrameworkRef,
  selectedmfStandard,
  viewHistory,
  setSelectedView,
  setViewHistory,
  setSelectedDr,
  parentMaterialMetrics,
  materialStandards,
  isCollectOnly,
  parentMaterialStandards,
}: {
  frameworkList: string[];
  selectedFrameworkRef: string;
  selectedmfStandard?: MultiFrameCategoryAndStandards[number]['mfStandards'][number];
  viewHistory?: JSX.Element;
  setSelectedView: Dispatch<SetStateAction<string>>;
  setViewHistory: Dispatch<SetStateAction<string[]>>;
  setSelectedDr: Dispatch<
    SetStateAction<
      | {
          name: string;
          ref: string;
          metrics?: Metric[];
        }
      | undefined
    >
  >;
  parentMaterialMetrics:
    | GetParentAssessmentMaterialMetricsQuery_['esrs_MaterialMetric']
    | undefined;
  materialStandards: GetMaterialStandardsIdsQuery_['esrs_MaterialStandard'] | undefined;
  isCollectOnly: boolean;
  parentMaterialStandards?: NonNullable<
    NonNullable<GetParentMaterialityAssessmentQuery_['EsrsAssessment_by_pk']>['parentAssessment']
  >['materialStandards'];
}) => {
  const { esrsAssessmentId = '', framework: assessmentFrameworkRef = '' } = useParams();
  const frameworkRefs = frameworkList.filter((framework) => {
    if (selectedFrameworkRef === 'all') {
      return framework !== 'all';
    }
    return framework === selectedFrameworkRef;
  });

  if (!frameworkRefs.includes(assessmentFrameworkRef)) {
    frameworkRefs.push(assessmentFrameworkRef);
  }

  const { data, loading } = useGetMFdrForMFstandardQuery({
    variables: {
      assessmentId: esrsAssessmentId,
      mfStandardRef: selectedmfStandard?.mfStandardRef,
      frameworkRefs,
      assessmentFrameworkRef,
    },
    skip: !esrsAssessmentId || !selectedmfStandard?.mfStandardRef || !assessmentFrameworkRef,
  });

  const frameworkStandard = useMemo(
    () =>
      selectedmfStandard?.standards.find(
        (standard) => standard.frameworkRef === assessmentFrameworkRef
      ),
    [selectedmfStandard, assessmentFrameworkRef]
  );

  const mfDrData = useMemo(() => {
    const mfDrData: {
      [key: string]: {
        name: string;
        ref: string;
        metrics: Metric[];
      };
    } = {};
    data?.MFStandard?.[0]?.mfDisclosureRequirementGroups?.forEach((mfDrGroup) => {
      mfDrGroup?.mfDisclosureRequirements?.forEach((mfDrRequirement) =>
        mfDrRequirement?.mfDisclosureRequirementItems?.forEach((mfDrItem) => {
          if (mfDrData?.hasOwnProperty(mfDrRequirement.reference)) {
            if (
              !mfDrItem.metric.requirement.isMandatory ||
              mfDrItem.metric.requirement.frameworkRef !== assessmentFrameworkRef
            ) {
              mfDrData[mfDrRequirement.reference].metrics.push(mfDrItem.metric);
            }
          } else {
            mfDrData[mfDrRequirement.reference] = {
              name: mfDrRequirement.title,
              ref: mfDrRequirement.reference,
              metrics:
                !mfDrItem.metric.requirement.isMandatory ||
                mfDrItem.metric.requirement.frameworkRef !== assessmentFrameworkRef
                  ? [mfDrItem.metric]
                  : [],
            };
          }
        })
      );
    });
    return mfDrData;
  }, [data, assessmentFrameworkRef]);

  const mfMetricsData = useMemo(() => {
    return Object.keys(mfDrData)
      .flatMap((mfDr) =>
        mfDrData[mfDr].metrics
          .filter((met) => met.isAssessable)
          .sort((a, b) => sortFrameworkMetrics(a, b, assessmentFrameworkRef))
          .map((metric) => {
            const isOtherFramework = metric.requirement.frameworkRef !== assessmentFrameworkRef;
            const commonFrameworkMetricStandard = isOtherFramework
              ? mfDrData[mfDr].metrics.find(
                  (m) => m.requirement.frameworkRef === assessmentFrameworkRef
                )?.requirement.group.standard.reference
              : metric.requirement.group.standard.reference;

            const materialStandardId =
              materialStandards?.find((ms) => ms.standardRef === commonFrameworkMetricStandard)
                ?.id ?? metric.materialMetrics?.[0]?.materialStandardId;

            return {
              title: metric.shortTitle ?? metric.title,
              type: metric.metricType,
              metric: metric,
              framework: (metric.requirement.group.standard.frameworkRef ??
                FrameworkRefs.esrs) as FrameworkRefs,
              reference: metric.reference,
              subCategory: mfDrData[mfDr].name,
              description: metric.description,
              materialStandardId,
            };
          })
      )
      .filter((met) => selectedFrameworkRef === 'all' || met.framework === selectedFrameworkRef);
  }, [
    mfDrData,
    materialStandards,
    selectedmfStandard,
    assessmentFrameworkRef,
    selectedFrameworkRef,
  ]);

  const isNotAssessedOrNotMaterial =
    frameworkStandard?.materiality === StandardMaterialityState.toAssess ||
    frameworkStandard?.materiality === StandardMaterialityState.notMaterial;

  const frameworkStandardName = frameworkStandard?.standardName;
  const frameworkName = Frameworks[assessmentFrameworkRef as keyof typeof Frameworks];

  if (loading) {
    return <Loader />;
  }
  return (
    <VStack
      gap={'40px'}
      justifyItems={'start'}
      alignSelf={'start'}
      padding={'24px 16px'}
      width={'100%'}
      alignItems={'start'}
      overflow="hidden"
    >
      <VStack
        gap={'16px'}
        alignItems={'start'}
        width={'100%'}
        borderBottom={'1px solid'}
        borderColor={'border.decorative'}
        paddingBottom={'32px'}
      >
        {viewHistory}
        <HStack justifyContent={'space-between'} width={'100%'}>
          <HStack>
            <Typography variant="h1">{selectedmfStandard?.mfStandardName}</Typography>
            <Typography color={'text.hint'}>{mfMetricsData.length}</Typography>
          </HStack>
          <MaterialityStatusBox materiality={frameworkStandard?.materiality as Materiality} />
        </HStack>
        {isNotAssessedOrNotMaterial && (
          <Infobox
            status="warning"
            withIcon={true}
            title={
              frameworkStandard?.materiality === StandardMaterialityState.toAssess
                ? 'Not assessed yet'
                : 'Assessed as not material'
            }
            description={
              frameworkStandard?.materiality === StandardMaterialityState.toAssess ? (
                <Typography>
                  You have not assessed materiality of a corresponding {frameworkName} standard{' '}
                  <b>— {frameworkStandardName}</b>. If you wish to add any of the data points from
                  this group to your report, go to the standard and change your assessment to either
                  “Material” or “Not material but collect data”. After this, you will be able to add
                  metrics from here to your report and they will appear under the{' '}
                  <b>{frameworkStandardName}</b> standard.
                </Typography>
              ) : (
                <Typography>
                  You have assessed a corresponding {frameworkName} standard{' '}
                  <b>— {frameworkStandardName} —</b> as <b>“Not material”</b>. If you wish to add
                  any of the data points from this group to your report, go to the standard and
                  change your assessment to either “Material” or “Not material but collect data”.
                  After this, you will be able to add metrics from here to your report and they will
                  appear under the <b>{frameworkStandardName}</b> standard.
                </Typography>
              )
            }
            closable={false}
            extra={
              <Button
                variant={'secondary'}
                rightIcon={<ArrowUpRightIcon color={'text.action'} />}
                onClick={() =>
                  window.open(`onboarding/materiality/standard/${frameworkStandard.standardRef}`)
                }
              >
                Open standard
              </Button>
            }
          />
        )}
        <Grid templateColumns="repeat(3, 1fr)" gap="8px" width={'100%'}>
          {Object.keys(mfDrData)?.map((dr) => (
            <GridItem>
              <HStack
                padding={'16px'}
                gap={'8px'}
                borderRadius={'10px'}
                shadow="interactive"
                bg="bg.elevated.interactive"
                _hover={{
                  shadow: 'interactive.hover',
                  background: 'bg.elevated.interactive.hover',
                }}
                cursor="pointer"
                justifyContent={'space-between'}
                height={'52px'}
                onClick={() => {
                  setSelectedView('drView');
                  setSelectedDr(mfDrData[dr]);
                  setViewHistory((prevViewHistory) => [...prevViewHistory, mfDrData[dr].name]);
                }}
              >
                <TruncatableText fontSize={'14px'} fontWeight={'600'} text={mfDrData[dr].name} />
                <Typography
                  textAlign={'right'}
                  fontSize={'14px'}
                  fontWeight={'400'}
                  color={'text.hint'}
                >
                  {
                    mfDrData[dr].metrics.filter(
                      (met) =>
                        met.isAssessable &&
                        (selectedFrameworkRef === 'all' ||
                          met.requirement.frameworkRef === selectedFrameworkRef)
                    ).length
                  }
                </Typography>
              </HStack>
            </GridItem>
          ))}
        </Grid>
      </VStack>
      <MFStandardBrowserTable
        mfDisclosures={mfMetricsData}
        isCollectOnly={isCollectOnly}
        parentMaterialMetrics={parentMaterialMetrics}
        hasPhasedInOption={false}
        isNotAssessedOrNotMaterial={isNotAssessedOrNotMaterial}
        isDrView={false}
        parentMaterialStandards={parentMaterialStandards}
      />
    </VStack>
  );
};
